/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The terms as VALUES instead of icons (plan-voorwaarden-vergelijking-2026-10-07
 * §5.2 + §6): the ONE presentation module behind every surface that prints the
 * terms — the collapsed row's Voorwaarden cell (two short lines), its
 * Uitbetaling cell (frequency + timing), its smart-charger mark, and the fact
 * table the expanded panel, the compare pages and the profile page share
 * (question · value · tone), closed by the review line.
 *
 * Every value copies its fact's tone from the one tone table
 * (src/lib/termsTone.ts) — never a tone of its own — and carries the `topic`
 * whose tone it shows, so a test can hold it against the derived bullets
 * (src/lib/termsVerdict.ts): emerald value ⇔ a pluspunt, amber ⇔ a watch
 * aandachtspunt, muted ⇔ a "niet gecontroleerd / niet vermeld" aandachtspunt.
 *
 * The rules the sentence digest learned survive here as rules of the values:
 * a value never says more than its fact (a coded fact's note qualifies it, an
 * `other` note IS the value), and the row's looptijd enters only where it is
 * the fact shown ("Vast 3 jaar"). When the review is not `read`, the binding
 * questions are not answered at all — one status line stands in for them.
 *
 * Pure on purpose (the partnerCompleteness.ts pattern): no React, no i18n
 * strings, no number formatting. Every text is a full i18n KEY with typed
 * variables (TermsVar) the renderer formats per locale; the keys live in the
 * `offer_terms.*` namespace (code-owned, outside the CMS), plus a few shared
 * ones (`table.payout_values.*`, `dossier.value.priceModel.*`, the question
 * labels `table.duration` / `table.payout`).
 *
 * COPY RULES that follow from the row's measured widths (§6.1, Phase 0, the
 * copy review of 2026-10-08): the xl cell is 8.75 rem (140 px) and every line
 * the module can emit fits it at the row's type — the widest are pinned by name
 * in termsPresentation.test.ts ("Opzegdatum ontbreekt" 135 px; a character
 * count is no width rule). ONE date convention: line 1 names a deadline on a
 * day ≥ 10 by its LAST day ("Opzeggen t/m 29 sep", EN "Cancel by 29 Sept"),
 * one on a day ≤ 9 as "Opzeggen vóór 1 okt" — the same fact; the fact table
 * and the bullets always name the first day too late ("vóór 30 september", EN
 * "before 30 September").
 */

import type {
  OfferPricing,
  OfferPricingTier,
  OfferTermField,
  PublicDossier,
  PublicDossierEntry,
  PublicTermsReview,
} from '../types';
import { dossierValueLabelKey, monthDayEve, monthDayParts } from './dossier.js';
import { canonicalPayout, payoutValueKey } from './payout';
import {
  contractYears,
  factState,
  factTone,
  isCancelMoot,
  termsReviewStatus,
  type FactTone,
  type ToneField,
} from './termsTone';

/**
 * The fact whose tone a value shows (a ToneField), or a verdict-only topic:
 * the KvK number (D23), the service costs (D28, judged outside the tone
 * table), a comparison's relative pluspunten (D36, src/lib/relativeBullets.ts
 * — judged against the offers beside it, no fact of the offer's own), the
 * redactie's own bullet, and the offer's pricing model (the "Verdienmodel"
 * aandachtspunt that leads every offer's list, src/lib/termsVerdict.ts: a
 * fact of the offer record, neutral — the net figure is where a model is
 * weighed).
 */
export type TermsTopic =
  | ToneField
  | 'kvk'
  | 'serviceCosts'
  | 'relative'
  | 'editorial'
  | 'pricingModel';

/**
 * A question of the fact table: a toned fact. (The offer's pricing model had a
 * row here on 2026-10-15 for a staffel and a vastrecht offer; since the same
 * day every offer's model is its first aandachtspunt instead — the panel says
 * it once.)
 */
export type TermsQuestion = ToneField;

/** A raw value the renderer formats for the active locale. */
export type TermsVar =
  /** A yearless day (MMDD) → formatMonthDay(monthDay, lang, style). */
  | { kind: 'date'; monthDay: number; style: 'long' | 'short' }
  /** An ISO instant → formatDateShort (the review's date). */
  | { kind: 'iso_date'; value: string }
  /** Whole euros print without decimals, anything else with two. */
  | { kind: 'eur'; value: number }
  /** A per-unit rate: always two decimals. */
  | { kind: 'eur_rate'; value: number }
  | { kind: 'kwh'; value: number }
  | { kind: 'count'; value: number }
  /** A number of months → `offer_terms.unit.months_one` / `offer_terms.unit.months`. */
  | { kind: 'months'; value: number }
  /** A payout frequency → `table.payout_values.<key>`. */
  | { kind: 'payout'; key: string }
  /** Signup requirement codes → `dossier.value.signupRequirements.<code>`; `other` = the note. */
  | { kind: 'items'; codes: string[]; note: string }
  /** Verbatim text (a provider name, a frequency outside the vocabulary). */
  | { kind: 'text'; value: string }
  /** An offer's pricing model → its compact description (src/lib/offerPricingText.ts). */
  | { kind: 'offer_pricing'; pricing: OfferPricing }
  /** A commission percentage as the row prints it, without its "%" (formatCommissionPct). */
  | { kind: 'pct'; value: number }
  /** A € per kWh of a pricing model, at the editors' four decimals (formatKwhRate). */
  | { kind: 'eur_kwh'; value: number }
  /**
   * A staffel's bands, drawn as a ladder where a surface can (DerivedBullets →
   * StaffelLadder: one row per band, the band `atKwh` falls in emphasised) and
   * as the inline description everywhere text is all there is (termsText).
   */
  | { kind: 'ladder'; tiers: OfferPricingTier[]; atKwh: number };

/** A full i18n key with its variables, or text a person wrote (an `other` note). */
export type TermsText = { key: string; vars?: Record<string, TermsVar> } | { text: string };

/** One line of the collapsed row's Voorwaarden cell. */
export interface TermsRowLine {
  /** The fact whose tone this line shows. */
  topic: TermsTopic;
  key: string;
  vars?: Record<string, TermsVar>;
  tone: FactTone;
  /** The full value, for the line's `title` (the row truncates). */
  full: TermsText;
}

export interface TermsRowCell {
  /** Two lines (deadline, then term) under a `read` review; ONE status line otherwise. */
  lines: TermsRowLine[];
}

/** One line of the Uitbetaling cell, in its own tone. */
export interface TermsPayoutLine {
  /** `payoutTiming` when the line carries the timing, else the frequency (neutral). */
  topic: 'payoutTiming' | 'payoutFrequency';
  text: TermsText;
  tone: FactTone;
}

/** The Uitbetaling cell: the frequency, with the payout timing as its sub-line. */
export interface TermsPayoutCell {
  /**
   * The main line: the frequency — or, when only the timing is known, the
   * timing ("Na het jaar": a dash over a sub-line read as broken). Null when
   * neither is known (the renderer's own unknown dash).
   */
  main: TermsPayoutLine | null;
  /**
   * The small timing line under a known frequency ("na het jaar"); null when
   * the timing is not one of the two the row can say, or is the main line.
   */
  sub: TermsPayoutLine | null;
}

/** The smart-charger mark: nothing when a smart charger is required (the norm). */
export interface TermsSmartMark {
  topic: 'smartChargingRequired';
  key: string;
  /** The phone card's wording, which sits under the VOORWAARDEN label and must name itself. */
  cardKey: string;
  tone: FactTone;
}

/** One question of the fact table. */
export interface TermsFactRow {
  /** Which question the row answers (its position and its label). */
  question: TermsQuestion;
  /** The fact whose tone the value shows — the question itself, except a moot deadline. */
  topic: TermsTopic;
  /** The question's label key. */
  label: string;
  value: TermsText;
  tone: FactTone;
  /** A note that QUALIFIES a coded fact (an `other` note is the value itself). */
  note?: string;
}

/** The review as one sentence, closing the table (or standing in for its terms). */
export interface TermsReviewLine {
  topic: 'termsReview';
  key: string;
  vars?: Record<string, TermsVar>;
  tone: FactTone;
}

export interface TermsFactTable {
  /** In question order. Under a review that is not `read`, the binding questions are absent. */
  rows: TermsFactRow[];
  review: TermsReviewLine;
}

const NS = 'offer_terms';
const rowKey = (leaf: string) => `${NS}.row.${leaf}`;
const valueKey = (leaf: string) => `${NS}.value.${leaf}`;

/** The question label per question. Looptijd and Uitbetaling reuse the table's own. */
export const TERMS_QUESTION_LABEL: Readonly<Record<ToneField, string>> = {
  duration: 'table.duration',
  cancelDeadline: `${NS}.q.cancel`,
  afterTerm: `${NS}.q.after_term`,
  exitCost: `${NS}.q.exit_cost`,
  payoutTiming: 'table.payout',
  inboeker: `${NS}.q.inboeker`,
  smartChargingRequired: `${NS}.q.smart`,
  minimumVolume: `${NS}.q.minimum`,
  signupRequirements: `${NS}.q.signup`,
  priceModel: `${NS}.q.price_model`,
  termsReview: `${NS}.q.review`,
};

/** The fact table's question order — the compare pages' order (§6.3). */
export const TERMS_QUESTIONS: readonly ToneField[] = [
  'duration',
  'cancelDeadline',
  'afterTerm',
  'exitCost',
  'payoutTiming',
  'inboeker',
  'smartChargingRequired',
  'minimumVolume',
  'signupRequirements',
  'priceModel',
  'termsReview',
];

/** The question an offer's own term answers: its rhythm is part of the Uitbetaling question. */
export const offerTermQuestion = (field: OfferTermField): TermsQuestion =>
  field === 'payoutFrequency' ? 'payoutTiming' : field;

type Dossier = PublicDossier | Partial<Record<string, PublicDossierEntry>> | null | undefined;

const code = (entry: PublicDossierEntry | null | undefined): string | null =>
  entry && typeof entry.value === 'string' ? entry.value : null;

const amountOf = (entry: PublicDossierEntry | null | undefined): number | null =>
  entry && typeof entry.amount === 'number' && Number.isFinite(entry.amount) ? entry.amount : null;

/** The note of a CODED fact; an `other` note is the fact itself, not a qualifier. */
const qualifier = (entry: PublicDossierEntry | null | undefined): string | undefined =>
  entry && entry.note && code(entry) !== 'other' ? entry.note : undefined;

/**
 * `{ date, date_eve }` for a deadline: the fact table and the bullets say
 * "vóór {date}" / "before {date}" (the first day too late, in both languages);
 * only the row's through-form for a day ≥ 10 names the eve ("t/m {date_eve}" /
 * "by {date_eve}").
 */
function deadlineVars(monthDay: number, style: 'long' | 'short'): Record<string, TermsVar> {
  const eve = monthDayEve(monthDay);
  return {
    date: { kind: 'date', monthDay, style },
    ...(eve !== null ? { date_eve: { kind: 'date', monthDay: eve, style } as TermsVar } : {}),
  };
}

const NOT_CHECKED_VALUE: TermsText = { key: valueKey('not_checked') };
const NOT_STATED_VALUE: TermsText = { key: valueKey('not_stated') };

// ── The answer per question (shared by the row and the table) ─────────────────

interface Answer {
  topic: TermsTopic;
  tone: FactTone;
  value: TermsText;
  note?: string;
}

/** Opzeggen. Moot when the offer ends by itself (isCancelMoot). */
function cancelAnswer(
  dossier: Dossier
): Answer & { rowKey: string; rowVars?: Record<string, TermsVar> } {
  if (isCancelMoot(dossier)) {
    return {
      topic: 'afterTerm',
      tone: factTone('afterTerm', dossier?.afterTerm),
      value: { key: valueKey('cancel_not_needed') },
      rowKey: rowKey('cancel_not_needed'),
    };
  }
  const entry = dossier?.cancelDeadline;
  const tone = factTone('cancelDeadline', entry);
  const base = { topic: 'cancelDeadline' as const, tone, note: qualifier(entry) };
  if (factState('cancelDeadline', entry) === 'not_checked') {
    return { ...base, value: NOT_CHECKED_VALUE, rowKey: rowKey('not_checked'), note: undefined };
  }
  switch (code(entry)) {
    case 'none':
      return { ...base, value: { key: valueKey('cancel_none') }, rowKey: rowKey('cancel_until') };
    case 'before_date': {
      const monthDay = amountOf(entry) as number;
      const parts = monthDayParts(monthDay)!;
      // The row's one convention (UI review #4): a one-digit day as "vóór 1
      // okt", a two-digit day as the last day it may still arrive, "t/m" /
      // "by" — the same fact, in the width the cell has.
      const short = deadlineVars(monthDay, 'short');
      const row =
        parts.day <= 9
          ? { rowKey: rowKey('cancel_before'), rowVars: short }
          : { rowKey: rowKey('cancel_through'), rowVars: short };
      const long = deadlineVars(monthDay, 'long');
      const value: TermsText =
        parts.day === 1
          ? {
              key: valueKey('cancel_before'),
              vars: { ...long, months: { kind: 'months', value: 13 - parts.month } },
            }
          : { key: valueKey('cancel_before_day'), vars: long };
      return { ...base, value, ...row };
    }
    case 'not_stated':
      return { ...base, value: NOT_STATED_VALUE, rowKey: rowKey('cancel_not_stated') };
    default:
      // `other`: the note is the fact.
      return {
        ...base,
        value: { text: entry?.note ?? '' },
        rowKey: rowKey('cancel_see_note'),
        note: undefined,
      };
  }
}

/** Na de looptijd (the table: the afterTerm fact alone). */
function afterTermAnswer(dossier: Dossier, years: number | null): Answer {
  const entry = dossier?.afterTerm;
  const tone = factTone('afterTerm', entry, { contractDuration: years });
  const base = { topic: 'afterTerm' as const, tone, note: qualifier(entry) };
  if (factState('afterTerm', entry) === 'not_checked') {
    return { ...base, value: NOT_CHECKED_VALUE, note: undefined };
  }
  switch (code(entry)) {
    case 'ends':
      return { ...base, value: { key: valueKey('after_ends') } };
    case 'renews_yearly':
      return { ...base, value: { key: valueKey('after_yearly') } };
    case 'renews_same_term':
      // A 1-year offer renewing for its own term renews per year: it reads as
      // the yearly renewal (neutral, termsTone). Otherwise the plain wording —
      // never a looptijd number in a value (the row's looptijd is its own fact).
      return {
        ...base,
        value: { key: valueKey(years === 1 ? 'after_yearly' : 'after_same_term') },
      };
    case 'not_stated':
      return { ...base, value: NOT_STATED_VALUE };
    default:
      return { ...base, value: { text: entry?.note ?? '' }, note: undefined };
  }
}

/** Eerder stoppen. */
function exitAnswer(dossier: Dossier): Answer {
  const entry = dossier?.exitCost;
  const tone = factTone('exitCost', entry);
  const base = { topic: 'exitCost' as const, tone, note: qualifier(entry) };
  if (factState('exitCost', entry) === 'not_checked') {
    return { ...base, value: NOT_CHECKED_VALUE, note: undefined };
  }
  switch (code(entry)) {
    case 'none':
      return { ...base, value: { key: valueKey('exit_none') } };
    case 'forfeit_year_payout':
      return { ...base, value: { key: valueKey('exit_forfeit') } };
    case 'fee':
      return {
        ...base,
        value: {
          key: valueKey('exit_fee'),
          vars: { amount: { kind: 'eur', value: amountOf(entry) as number } },
        },
      };
    case 'not_stated':
      return { ...base, value: NOT_STATED_VALUE };
    default:
      return { ...base, value: { text: entry?.note ?? '' }, note: undefined };
  }
}

type FrequencyVar = Extract<TermsVar, { kind: 'payout' } | { kind: 'text' }>;

/**
 * The frequency as a variable: its label key, a value outside the vocabulary
 * verbatim (canonicalPayout keeps a typo visible), or null when unknown —
 * "Onbekend", "Periodiek" and "Per afspraak" included (isUnknownPayout).
 */
function frequencyVar(payoutFrequency: unknown): FrequencyVar | null {
  const raw = typeof payoutFrequency === 'string' ? payoutFrequency : null;
  const key = payoutValueKey(raw);
  if (key) return { kind: 'payout', key };
  const canonical = canonicalPayout(raw);
  return canonical ? { kind: 'text', value: canonical } : null;
}

/** The frequency alone, as a value. */
const frequencyText = (freq: FrequencyVar): TermsText =>
  freq.kind === 'payout' ? { key: `table.payout_values.${freq.key}` } : { text: freq.value };

/**
 * Uitbetaling: the frequency and the timing, one question. An offer's own rhythm
 * brings its own note ("of per jaar, naar keuze"), which leads whatever the
 * timing's note says.
 */
function payoutAnswer(dossier: Dossier, payoutFrequency: unknown): Answer {
  const answer = payoutFactsAnswer(dossier, payoutFrequency);
  const rhythmNote = dossier?.payoutFrequency?.note;
  if (!rhythmNote) return answer;
  return { ...answer, note: answer.note ? `${rhythmNote} · ${answer.note}` : rhythmNote };
}

function payoutFactsAnswer(dossier: Dossier, payoutFrequency: unknown): Answer {
  const entry = dossier?.payoutTiming;
  const tone = factTone('payoutTiming', entry);
  const freq = frequencyVar(payoutFrequency);
  const base = { topic: 'payoutTiming' as const, tone, note: qualifier(entry) };
  switch (factState('payoutTiming', entry) === 'not_checked' ? null : code(entry)) {
    case 'during_year':
      return {
        ...base,
        value: freq
          ? { key: valueKey('paid_during'), vars: { frequency: freq } }
          : { key: valueKey('paid_during_only') },
      };
    case 'after_year':
      return {
        ...base,
        value: freq
          ? { key: valueKey('paid_after'), vars: { frequency: freq } }
          : { key: valueKey('paid_after_only') },
      };
    case 'not_stated':
      return {
        ...base,
        value: freq
          ? { key: valueKey('paid_timing_not_stated'), vars: { frequency: freq } }
          : NOT_STATED_VALUE,
      };
    case 'other':
      // The timing's note is the fact; next to a known frequency it qualifies it.
      return freq
        ? { ...base, value: frequencyText(freq), note: entry?.note || undefined }
        : { ...base, value: { text: entry?.note ?? '' }, note: undefined };
    default:
      // Timing not checked: a known frequency is still a known fact (neutral);
      // with neither, the whole question is unchecked.
      return freq
        ? { ...base, tone: 'neutral', value: frequencyText(freq), note: undefined }
        : { ...base, tone: 'unknown', value: NOT_CHECKED_VALUE, note: undefined };
  }
}

/**
 * Inboeker (D24): who registers the kWh with the NEa. Answered when filed, and
 * when implied: an unfiled one on a party that is not on the NEa list reads as
 * "via another inboekdienstverlener" (termsTone's neaListed rule). Anything
 * else unfiled is NO row (null): "Niet gecontroleerd" there tells a visitor
 * nothing, costs the open panel a line it does not have (an 8th row took it to
 * 351 px at 1280, the target is 335) and would stand on every register-only
 * profile page — so the panel, the profile page and the compare pages all
 * leave it out (UI review #5). The note of `other` names the party, when
 * published.
 */
function inboekerAnswer(dossier: Dossier, neaListed: boolean | null | undefined): Answer | null {
  const entry = dossier?.inboeker;
  const tone = factTone('inboeker', entry, { neaListed });
  const base = { topic: 'inboeker' as const, tone };
  const filed = factState('inboeker', entry) !== 'not_checked';
  const value = filed ? code(entry) : tone === 'watch' ? 'other' : null;
  switch (value) {
    case 'self':
      return { ...base, value: { key: valueKey('inboeker_self') } };
    case 'other': {
      const name = filed && entry?.note ? entry.note : '';
      return {
        ...base,
        value: name
          ? { key: valueKey('inboeker_other_named'), vars: { name: { kind: 'text', value: name } } }
          : { key: valueKey('inboeker_other') },
      };
    }
    case 'not_stated':
      return { ...base, value: NOT_STATED_VALUE };
    default:
      // Nothing filed, nothing implied.
      return null;
  }
}

/** Slimme laadpaal. */
function smartAnswer(required: boolean | null | undefined): Answer {
  const tone = factTone('smartChargingRequired', required);
  const value: TermsText =
    required === true
      ? { key: valueKey('smart_required') }
      : required === false
        ? { key: valueKey('smart_optional') }
        : NOT_CHECKED_VALUE;
  return { topic: 'smartChargingRequired', tone, value };
}

/**
 * Minimale afname — a row only when the terms STATE a minimum (Jasper,
 * 2026-10-08: "remove minimale afname, unless it IS mentioned, then it is an
 * aandachtspunt"). No minimum, a silent text, nothing filed and a minimum of
 * one charge point print no row at all; it is not one of the questions a
 * review must answer. The compare model asks for `all` answers: next to an
 * offer that states a minimum, "Geen minimum" is the other offer's true cell.
 */
function minimumAnswer(
  dossier: Dossier,
  annualKwh: number | null | undefined,
  all = false
): Answer | null {
  const entry = dossier?.minimumVolume;
  const tone = factTone('minimumVolume', entry, { annualKwh });
  if (factState('minimumVolume', entry) === 'not_checked') return null;
  const base = { topic: 'minimumVolume' as const, tone, note: qualifier(entry) };
  const amount = amountOf(entry);
  switch (code(entry)) {
    case 'none':
      return all ? { ...base, value: { key: valueKey('minimum_none') } } : null;
    case 'not_stated':
      return all ? { ...base, value: NOT_STATED_VALUE } : null;
    case 'kwh_per_year':
      return {
        ...base,
        value: {
          key: valueKey('minimum_kwh'),
          vars: { min: { kind: 'kwh', value: amount as number } },
        },
      };
    case 'charge_points':
      if ((amount as number) <= 1) {
        return all ? { ...base, value: { key: valueKey('minimum_points_one') } } : null;
      }
      return {
        ...base,
        value: {
          key: valueKey('minimum_points'),
          vars: { count: { kind: 'count', value: amount as number } },
        },
      };
    default:
      return { ...base, value: { text: entry?.note ?? '' }, note: undefined };
  }
}

/** Nodig bij aanmelden — only when known. */
function signupAnswer(dossier: Dossier): Answer | null {
  const entry = dossier?.signupRequirements;
  if (!entry) return null;
  const codes = Array.isArray(entry.value) ? entry.value : [entry.value];
  if (codes.length === 0) return null;
  return {
    topic: 'signupRequirements',
    tone: factTone('signupRequirements', entry),
    value: {
      key: valueKey('signup_items'),
      vars: { items: { kind: 'items', codes: [...codes], note: entry.note } },
    },
    // With `other` in the list the note IS one of the items.
    note: codes.includes('other') ? undefined : entry.note || undefined,
  };
}

/** Prijsmodel — only when known; the consumer decides where it shows. */
function priceAnswer(dossier: Dossier): Answer | null {
  const entry = dossier?.priceModel;
  const tone = factTone('priceModel', entry);
  if (factState('priceModel', entry) === 'not_checked') return null;
  const base = { topic: 'priceModel' as const, tone, note: qualifier(entry) };
  const value = code(entry) as string;
  const amount = amountOf(entry);
  if (value === 'other') return { ...base, value: { text: entry?.note ?? '' }, note: undefined };
  if (value === 'not_stated') return { ...base, value: NOT_STATED_VALUE };
  if ((value === 'fixed_per_kwh' || value === 'fixed_per_ere') && amount !== null) {
    return {
      ...base,
      value: {
        key: valueKey(value === 'fixed_per_kwh' ? 'price_fixed_kwh' : 'price_fixed_ere'),
        vars: { amount: { kind: 'eur_rate', value: amount } },
      },
    };
  }
  return {
    ...base,
    value: {
      key: `dossier.value.priceModel.${dossierValueLabelKey('priceModel', value, 'visitor')}`,
    },
  };
}

/** Looptijd — only on a row (a provider has no single looptijd). */
function durationAnswer(years: number | null): Answer | null {
  if (years === null) return null;
  return {
    topic: 'duration',
    tone: factTone('duration', years),
    value:
      years >= 2
        ? { key: valueKey('fixed_years'), vars: { years: { kind: 'count', value: years } } }
        : { key: valueKey('one_year') },
  };
}

/** Voorwaarden gelezen, as a table value. */
function reviewAnswer(review: PublicTermsReview | null | undefined): Answer {
  const status = termsReviewStatus(review);
  const tone = factTone('termsReview', review);
  const value: TermsText =
    status === 'read'
      ? { key: valueKey('read_on'), vars: { date: { kind: 'iso_date', value: review!.checkedAt } } }
      : status === 'not_published'
        ? { key: valueKey('not_published') }
        : status === 'unreadable'
          ? { key: valueKey('unreadable') }
          : NOT_CHECKED_VALUE;
  return { topic: 'termsReview', tone, value };
}

// ── The collapsed row ─────────────────────────────────────────────────────────

/** Line 1 of the Voorwaarden cell: the cancel deadline (exported for the glyph adapter). */
export function cancelRowLine(dossier: Dossier): TermsRowLine {
  const a = cancelAnswer(dossier);
  return {
    topic: a.topic,
    key: a.rowKey,
    ...(a.rowVars ? { vars: a.rowVars } : {}),
    tone: a.tone,
    full: a.value,
  };
}

/**
 * Line 2: the term. A looptijd of two years or more wins ("Vast 3 jaar" — the
 * lock-in is the fact that matters), unless the offer renews for that same
 * term ("Verlengt per 3 jaar"); otherwise what happens after the term.
 */
export function afterTermRowLine(dossier: Dossier, contractDuration?: unknown): TermsRowLine {
  const years = contractYears(contractDuration);
  const after = afterTermAnswer(dossier, years);
  if (years !== null && years >= 2) {
    const yearsVar: Record<string, TermsVar> = { years: { kind: 'count', value: years } };
    if (code(dossier?.afterTerm) === 'renews_same_term') {
      return {
        topic: 'afterTerm',
        key: rowKey('renews_same_term_years'),
        vars: yearsVar,
        tone: after.tone,
        full: after.value,
      };
    }
    const duration = durationAnswer(years)!;
    return {
      topic: 'duration',
      key: rowKey('fixed_years'),
      vars: yearsVar,
      tone: duration.tone,
      full: duration.value,
    };
  }
  const leaf =
    factState('afterTerm', dossier?.afterTerm) === 'not_checked'
      ? 'not_checked'
      : ((
          {
            ends: 'ends',
            renews_yearly: 'renews_yearly',
            // One year renewing for one year: "Verlengt per jaar" (afterTermAnswer).
            renews_same_term: years === 1 ? 'renews_yearly' : 'renews_same_term',
            not_stated: 'after_not_stated',
          } as Record<string, string>
        )[code(dossier?.afterTerm) as string] ?? 'after_see_note');
  return { topic: 'afterTerm', key: rowKey(leaf), tone: after.tone, full: after.value };
}

/** The status line that stands in for both lines when the terms were not read. */
function reviewRowLine(review: PublicTermsReview | null | undefined): TermsRowLine {
  const status = termsReviewStatus(review);
  const a = reviewAnswer(review);
  const leaf =
    status === 'not_published'
      ? 'not_published'
      : status === 'unreadable'
        ? 'unreadable'
        : 'not_checked';
  return { topic: 'termsReview', key: rowKey(leaf), tone: a.tone, full: a.value };
}

export interface TermsRowInput {
  /** The ROW's dossier: the provider's facts with the offer's overrides (withOfferTerms). */
  dossier: PublicDossier | null | undefined;
  /** The row's looptijd ('3 jaar'). */
  contractDuration?: string | null;
  termsReview: PublicTermsReview | null | undefined;
}

/** The Voorwaarden cell of one row. */
export function termsRowCell(input: TermsRowInput): TermsRowCell {
  if (termsReviewStatus(input.termsReview) !== 'read') {
    return { lines: [reviewRowLine(input.termsReview)] };
  }
  return {
    lines: [cancelRowLine(input.dossier), afterTermRowLine(input.dossier, input.contractDuration)],
  };
}

/** The Uitbetaling cell of one row (§6.1). */
export function termsPayoutCell(input: {
  dossier: PublicDossier | null | undefined;
  payoutFrequency: string | null | undefined;
}): TermsPayoutCell {
  const freq = frequencyVar(input.payoutFrequency);
  const entry = input.dossier?.payoutTiming;
  const tone = factTone('payoutTiming', entry);
  const timing = factState('payoutTiming', entry) === 'known' ? code(entry) : null;
  const during = timing === 'during_year';
  const known = during || timing === 'after_year';
  if (freq) {
    return {
      main: { topic: 'payoutFrequency', text: frequencyText(freq), tone: 'neutral' },
      sub: known
        ? {
            topic: 'payoutTiming',
            text: { key: rowKey(during ? 'paid_during' : 'paid_after') },
            tone,
          }
        : null,
    };
  }
  if (known) {
    return {
      main: {
        topic: 'payoutTiming',
        text: { key: rowKey(during ? 'paid_during_main' : 'paid_after_main') },
        tone,
      },
      sub: null,
    };
  }
  return { main: null, sub: null };
}

/**
 * The smart-charger mark (§6.1, D22). A required charger used to print nothing
 * (the market norm); since 2026-10-08 (Jasper: "show simply 'Ja' or 'Yes' on
 * the card, when a slimme laadpaal is required") it says so — "Ja" under the
 * column's word, "Slimme lader: ja" on the card, neutral.
 */
export function termsSmartMark(required: boolean | null | undefined): TermsSmartMark | null {
  const tone = factTone('smartChargingRequired', required);
  if (required === true) {
    return {
      topic: 'smartChargingRequired',
      key: rowKey('smart_required'),
      cardKey: rowKey('smart_required_card'),
      tone,
    };
  }
  if (required === false) {
    const key = rowKey('smart_optional');
    return { topic: 'smartChargingRequired', key, cardKey: key, tone };
  }
  return {
    topic: 'smartChargingRequired',
    key: rowKey('smart_not_checked'),
    cardKey: rowKey('smart_not_checked_card'),
    tone,
  };
}

// ── The fact table (panel, compare pages, profile page) ──────────────────────

export interface TermsFactInput {
  /** The row's merged dossier, or the provider's on the profile page. */
  dossier: PublicDossier | null | undefined;
  /** The row's looptijd; absent on the profile page (no Looptijd question then). */
  contractDuration?: string | null;
  /** The row's rhythm (offerPayoutFrequency), or the provider's on the profile page. */
  payoutFrequency: string | null | undefined;
  smartChargingRequired: boolean | null | undefined;
  termsReview: PublicTermsReview | null | undefined;
  /** The annual kWh this comparison calculates with (judges a kWh minimum). */
  annualKwh?: number | null;
  /** The record's NEa-list fact — an unfiled inboeker on `false` reads as "other" (D24). */
  neaListed?: boolean | null;
  /** For the review sentence ("De voorwaarden van {name} …"). */
  providerName?: string;
  /**
   * Whose words the facts are (`dossierProvenance`): folded into the review
   * sentence after a reading, so the panel and the profile page close on ONE
   * dated line. Absent = the plain "Voorwaarden gelezen op …" (compare pages).
   */
  provenance?: DossierProvenance | null;
}

const toRow = (question: TermsQuestion, a: Answer): TermsFactRow => ({
  question,
  topic: a.topic,
  label: TERMS_QUESTION_LABEL[question],
  value: a.value,
  tone: a.tone,
  ...(a.note ? { note: a.note } : {}),
});

/**
 * The review as one sentence. After a reading it says ONLY the latest edit and
 * who made it (Jasper 2026-10-16: "only show the latest edit date and by
 * whom"): "Laatst bijgewerkt door de redactie op …" (`read_updated`) — the
 * reading and every editorial entry are the redactie's — or "Laatst bijgewerkt
 * door Lekkerladen op …" (`read_updated_by_provider`) when a provider-confirmed
 * fact is newer. A tie goes to the redactie. An undated entry is no edit
 * (`dossierProvenance` skips it). Without a provenance (the compare pages) the
 * line stays "Voorwaarden gelezen op …" (`read`).
 */
export function termsReviewLine(
  review: PublicTermsReview | null | undefined,
  providerName = '',
  provenance?: DossierProvenance | null
): TermsReviewLine {
  const status = termsReviewStatus(review);
  const tone = factTone('termsReview', review);
  const name: TermsVar = { kind: 'text', value: providerName };
  const key = `${NS}.review.${status}`;
  if (status === 'not_checked') return { topic: 'termsReview', key, vars: { name }, tone };
  const date: TermsVar = { kind: 'iso_date', value: review!.checkedAt };
  if (status === 'read' && provenance) {
    const time = (iso: string | null) => (iso ? Date.parse(iso) : NaN);
    const redactie =
      time(provenance.editorial) > time(review!.checkedAt) ||
      !Number.isFinite(time(review!.checkedAt))
        ? (provenance.editorial ?? review!.checkedAt)
        : review!.checkedAt;
    const byProvider =
      provenance.provider !== null &&
      (time(provenance.provider) > time(redactie) || !Number.isFinite(time(redactie)));
    const at: TermsVar = { kind: 'iso_date', value: byProvider ? provenance.provider! : redactie };
    return byProvider
      ? {
          topic: 'termsReview',
          key: `${NS}.review.read_updated_by_provider`,
          vars: { name, date: at },
          tone,
        }
      : { topic: 'termsReview', key: `${NS}.review.read_updated`, vars: { date: at }, tone };
  }
  return {
    topic: 'termsReview',
    key,
    vars: status === 'read' ? { date } : { name, date },
    tone,
  };
}

/**
 * The fact table: every question in TERMS_QUESTIONS order. Always answered —
 * known, "niet vermeld" or "niet gecontroleerd" — are the binding questions
 * (under a `read` review), the payout, the smart charger and the review; the
 * inboeker when filed or implied (inboekerAnswer); Looptijd only on a row;
 * the minimum only when the terms state one (`minimumRow: 'all'` keeps "Geen
 * minimum" and "Niet vermeld" too — the compare model's cells), signup and
 * price model only when known. Under a review that is not `read` the binding
 * questions are left out and the review row/line stands in for them. The
 * offer's verdienmodel is no question here: it is the first aandachtspunt
 * (termsVerdict), so the panel says it once.
 */
export function termsFactTable(
  input: TermsFactInput,
  options: { minimumRow?: 'stated' | 'all' } = {}
): TermsFactTable {
  const years = contractYears(input.contractDuration);
  const read = termsReviewStatus(input.termsReview) === 'read';
  const answers: Partial<Record<ToneField, Answer | null>> = {
    duration: durationAnswer(years),
    cancelDeadline: read ? cancelAnswer(input.dossier) : null,
    afterTerm: read ? afterTermAnswer(input.dossier, years) : null,
    exitCost: read ? exitAnswer(input.dossier) : null,
    payoutTiming: payoutAnswer(input.dossier, input.payoutFrequency),
    inboeker: inboekerAnswer(input.dossier, input.neaListed),
    smartChargingRequired: smartAnswer(input.smartChargingRequired),
    minimumVolume: minimumAnswer(input.dossier, input.annualKwh, options.minimumRow === 'all'),
    signupRequirements: signupAnswer(input.dossier),
    priceModel: priceAnswer(input.dossier),
    termsReview: reviewAnswer(input.termsReview),
  };
  const rows: TermsFactRow[] = [];
  for (const question of TERMS_QUESTIONS) {
    const a = answers[question];
    if (a) rows.push(toRow(question, a));
  }
  return {
    rows,
    review: termsReviewLine(input.termsReview, input.providerName, input.provenance),
  };
}

/**
 * Whose words the facts are (D14): the latest dated provider-confirmed entry and
 * the latest dated editorial one across a dossier, as ISO instants — what the
 * panel's closing lines say ("Opgegeven door de aanbieder, bevestigd op …",
 * "Gecontroleerd door de redactie op …"; `dossier.provenance_*`, the date an
 * `iso_date` variable). The table's own review line says when the terms were
 * READ; these say who STATED what. An undated legacy entry earns no date — a
 * date we do not have is not one we make up.
 */
export interface DossierProvenance {
  provider: string | null;
  editorial: string | null;
}

export function dossierProvenance(
  /** A dossier — or the entries of several (the profile page adds the offers' overrides). */
  source: Dossier | ReadonlyArray<PublicDossierEntry | null | undefined>
): DossierProvenance {
  let provider: string | null = null;
  let editorial: string | null = null;
  const entries = Array.isArray(source)
    ? (source as ReadonlyArray<PublicDossierEntry | null | undefined>)
    : Object.values((source as Dossier) ?? {});
  for (const entry of entries) {
    if (!entry || !entry.at) continue;
    if (entry.source === 'provider') {
      if (!provider || entry.at > provider) provider = entry.at;
    } else if (!editorial || entry.at > editorial) {
      editorial = entry.at;
    }
  }
  return { provider, editorial };
}

// ── Key inventory (the coverage tests read it) ───────────────────────────────

/** Every `offer_terms.*` key this module can emit. */
export const TERMS_PRESENTATION_KEYS: readonly string[] = [
  ...[
    'cancel_not_needed',
    'cancel_until',
    'cancel_before',
    'cancel_through',
    'cancel_see_note',
    'cancel_not_stated',
    'fixed_years',
    'renews_same_term_years',
    'ends',
    'renews_yearly',
    'renews_same_term',
    'after_see_note',
    'after_not_stated',
    'not_checked',
    'not_published',
    'unreadable',
    'paid_during',
    'paid_after',
    'paid_during_main',
    'paid_after_main',
    'smart_required',
    'smart_required_card',
    'smart_optional',
    'smart_not_checked',
    'smart_not_checked_card',
  ].map(rowKey),
  ...[
    'cancel',
    'after_term',
    'exit_cost',
    'inboeker',
    'smart',
    'minimum',
    'signup',
    'price_model',
    'review',
  ].map((leaf) => `${NS}.q.${leaf}`),
  ...[
    'cancel_not_needed',
    'cancel_none',
    'cancel_before',
    'cancel_before_day',
    'after_ends',
    'after_yearly',
    'after_same_term',
    'exit_none',
    'exit_forfeit',
    'exit_fee',
    'paid_during',
    'paid_after',
    'paid_during_only',
    'paid_after_only',
    'paid_timing_not_stated',
    'inboeker_self',
    'inboeker_other',
    'inboeker_other_named',
    'smart_required',
    'smart_optional',
    'minimum_none',
    'minimum_kwh',
    'minimum_points',
    'minimum_points_one',
    'signup_items',
    'price_fixed_kwh',
    'price_fixed_ere',
    'fixed_years',
    'one_year',
    'not_stated',
    'not_checked',
    'read_on',
    'not_published',
    'unreadable',
  ].map(valueKey),
  ...[
    'read',
    'read_updated',
    'read_updated_by_provider',
    'not_checked',
    'not_published',
    'unreadable',
  ].map((leaf) => `${NS}.review.${leaf}`),
];

/**
 * `offer_terms.*` keys nothing emits any more but both baselines keep: a key
 * that shipped may be frozen in an environment's `i18n_admin/` by an admin
 * save, so it is retired, never reused or removed (docs/i18n-masking.md). The
 * fact table's "Verdienmodel" row (label + value, 2026-10-15) — its model is
 * the first aandachtspunt since the same day (termsVerdict `pricing_*`).
 */
export const RETIRED_OFFER_TERMS_KEYS: readonly string[] = [
  `${NS}.q.pricing_model`,
  `${NS}.value.pricing_model`,
];

/**
 * `offer_terms.*` keys the RENDERER uses around the emitted ones: the sr-only
 * tone words, the legend (the filter sheet's note — no table prints it, D26),
 * the source link of an editorial bullet and the months unit a `months`
 * variable formats through.
 */
export const OFFER_TERMS_RENDER_KEYS: readonly string[] = [
  `${NS}.tone.good`,
  `${NS}.tone.watch`,
  `${NS}.legend`,
  `${NS}.source`,
  `${NS}.source_quote`,
  `${NS}.unit.months_one`,
  `${NS}.unit.months`,
];
